import {
    STANDARD_KABOB_HEIGHT,
    TEMPLATE_REGENERATION_COMPLETE_MESSAGE,
    TEMPLATE_REGENERATION_TIMEOUT_MESSAGE,
    TEMPLATE_REGENERATION_FAILED_MESSAGE,
    TEMPLATE_REGENERATION_STATUS_CHECK_FAILED_MESSAGE,
} from "../../constants/CommonConstants";
import { ITemplateState, ICase, ICaseTemplate } from "../../types/types";
import {
    EpiqAdmin,
    EpiqProjectManager,
    EpiqTechSupport,
    EpiqTemplateAdmin,
    TemplatePermissions,
} from "../../constants/roles";

import { Alert, Box, Card, CardContent, Chip, CircularProgress, Grid, Theme, Typography } from "@mui/material";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import IconButton from "@mui/material/IconButton";
import { Link, useHistory } from "react-router-dom";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Skeleton from "@mui/material/Skeleton";
import makeStyles from "@mui/styles/makeStyles";
import {
    getTemplatesAction,
    downloadTemplateAction,
    approveTemplateAction,
    denyTemplateAction,
    deleteTemplateAction,
} from "./template.actions";
import {
    generateTemplateAsync,
    checkTemplateGenerationCompleteAsync,
    saveCaseTemplate,
    getTemplateDetailAsync,
} from "./template.api";
import { useTemplateGenerationPolling } from "../../hooks/useTemplateGenerationPolling";
import { showAlert } from "../Alerts/alerts.actions";
import { AlertType } from "../../types/enums";
import { IFilterDTO } from "../../types/types";
import SecuritiesTable, {
    IColumnHeader,
    ISecuritiesTableFieldMap,
    ISorter,
} from "../../components/Shared/SecuritiesTable/SecuritiesTable";
import { useContext } from "react";
import { UserContext, UserContextType } from "../User/UserContext";
import { getTemplateStatusText } from "../../utilities/templateStatus";
import { TemplateStatus, TemplateType } from "../../types/enums";
import DeleteTemplateModal from "./DeleteTemplateModal";

const useStyles = makeStyles((theme: Theme) => ({
    button: {
        background: "#006B86",
        size: "small",
        margin: theme.spacing(1),
        color: "white",
    },
    textLink: {
        color: theme.palette.text.primary,
        padding: "0.5em",
    },
    actionColumn: {
        width: "275px",
    },
    kabobIconEnable: {
        color: "#087C9B",
        filter: "dropShadow(1px 1px 2px rgba(0, 0, 0, 0.44))",
        "&:disabled": {
            color: "#BCC0C5",
        },
    },
}));

const templateListFilterColumns: IColumnHeader[] = [
    {
        field: "name",
        label: "Name",
        parameterName: "name",
        type: "string",
        value: "",
        allowFilter: true,
        minWidth: "256px",
    },
    {
        field: "templateStatus",
        label: "Status",
        parameterName: "statusText",
        type: "string",
        value: "",
        allowFilter: true,
        minWidth: "256px",
    },
    {
        field: "updatedOn",
        label: "Updated Date",
        parameterName: "updatedOn",
        type: "date",
        value: "",
        allowFilter: false,
    },
    {
        field: "updatedByUser",
        label: "Updated By",
        parameterName: "updatedOn",
        type: "date",
        value: "",
        allowFilter: false,
    },
    { field: "actions", label: "Actions" },
];

const defaultSort: ISorter = {
    property: "updatedOn",
    order: "desc",
};

let sortInfo: ISorter = { ...defaultSort };

interface TemplateListGridProps {
    selectTemplate: Function;
    searchName?: string;
    searchStatus?: string;
    handleDuplicateTemplate: Function;
    handleEditTemplate: Function;
}

const TemplateListGrid = ({
    selectTemplate,
    searchName,
    searchStatus,
    handleDuplicateTemplate,
    handleEditTemplate
}: TemplateListGridProps) => {
    const dispatch = useDispatch();
    const templateState: ITemplateState = useSelector(
        (state: any) => state.templateState
    );
    const classes = useStyles();
    let showKebobs = false;

    const history = useHistory();

    const getFilterFromProps = () => {
        let filter: IFilterDTO[] = [];

        if (searchName) {
            filter.push({
                parameter: "name",
                type: "string",
                value: searchName,
            });
        }

        if (searchStatus) {
            filter.push({
                parameter: "statusText",
                type: "string",
                value: searchStatus,
            });
        }

        return filter;
    };

    const addTemplateTypeFilter = (filter: IFilterDTO[]) => {
        filter.push({
            parameter: 'templateType',
            type: 'selectMany',
            value: `${TemplateType.Spreadsheet}`
        })
    }

    const initialFilter = getFilterFromProps();

    const { user, isInitialized, isAuthenticated, isInRole } = useContext(
        UserContext
    ) as UserContextType;

    const [kabobsMenu, setKabobsMenu] = React.useState<null | HTMLElement>(null);
    const [selectedRowIndex, setSelectedRowIndex] = useState<number>(-1);

    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
        null
    );
    const [selectedTemplateName, setSelectedTemplateName] = useState<string>("");

    // Tracks every templateId with an in-flight generate request or poll, so rows can be
    // generated concurrently/independently of one another (disabling/labelling only applies
    // to the row(s) actually generating, not the whole grid).
    const [generatingTemplateIds, setGeneratingTemplateIds] = useState<Set<number>>(new Set());

    const addGeneratingTemplateId = (templateId: number) => {
        setGeneratingTemplateIds(prev => {
            const next = new Set(prev);
            next.add(templateId);
            return next;
        });
    };

    const removeGeneratingTemplateId = (templateId: number) => {
        setGeneratingTemplateIds(prev => {
            if (!prev.has(templateId)) {
                return prev;
            }
            const next = new Set(prev);
            next.delete(templateId);
            return next;
        });
    };

    // Persistent, per-row generation result message. Unlike the toast (which auto-dismisses),
    // this stays on screen until the page is refreshed or a new Generate is triggered for that row.
    const [generationResultMessages, setGenerationResultMessages] = useState<Record<number, { severity: AlertType; message: string }>>({});

    const setGenerationResultMessage = (templateId: number, severity: AlertType, message: string) => {
        setGenerationResultMessages(prev => ({ ...prev, [templateId]: { severity, message } }));
    };

    const clearGenerationResultMessage = (templateId: number) => {
        setGenerationResultMessages(prev => {
            if (!(templateId in prev)) {
                return prev;
            }
            const next = { ...prev };
            delete next[templateId];
            return next;
        });
    };

    // For Associated templates (linked to one or more cases), regeneration must be run once per
    // associated case (passing both templateId and caseId), rather than the single-template call
    // used for Development/Approved templates. These refs track that fan-out so the row's
    // generating state/badge only clears once every associated case's generation has settled.
    // caseId -> { templateId, caseName }: identifies which row/template a given case-generation belongs to.
    const caseGenerationContextRef = useRef<Map<number, { templateId: number; caseName: string }>>(new Map());
    // templateId -> remaining pending case count + names of any cases that failed, for aggregating
    // the row's final generating/message state once all associated cases have settled.
    const templateCaseProgressRef = useRef<Map<number, { pending: number; failedCases: string[] }>>(new Map());

    const handleCaseGenerationSettled = (caseId: number, succeeded: boolean) => {
        const context = caseGenerationContextRef.current.get(caseId);
        if (!context) {
            return;
        }
        caseGenerationContextRef.current.delete(caseId);

        const { templateId } = context;
        const progress = templateCaseProgressRef.current.get(templateId);
        if (!progress) {
            return;
        }

        progress.pending -= 1;
        if (!succeeded) {
            progress.failedCases.push(context.caseName);
        }

        if (progress.pending <= 0) {
            templateCaseProgressRef.current.delete(templateId);
            removeGeneratingTemplateId(templateId);

            if (progress.failedCases.length > 0) {
                const message = `Failed to generate the template for the following case(s): ${progress.failedCases.join(', ')}.`;
                setGenerationResultMessage(templateId, AlertType.error, message);
                dispatch(showAlert({
                    hidden: false,
                    severity: AlertType.error,
                    message,
                }));
            } else {
                dispatch(showAlert({
                    hidden: false,
                    severity: AlertType.success,
                    message: TEMPLATE_REGENERATION_COMPLETE_MESSAGE,
                }));
            }
        }
    };

    const { startPolling: startTemplateGenerationPolling } = useTemplateGenerationPolling({
        checkCompletion: checkTemplateGenerationCompleteAsync,
        onCompleted: (templateId: number) => {
            removeGeneratingTemplateId(templateId);
            dispatch(showAlert({
                hidden: false,
                severity: AlertType.success,
                message: TEMPLATE_REGENERATION_COMPLETE_MESSAGE,
            }));
        },
        onTimedOut: (templateId: number) => {
            removeGeneratingTemplateId(templateId);
            setGenerationResultMessage(templateId, AlertType.warning, TEMPLATE_REGENERATION_TIMEOUT_MESSAGE);
            dispatch(showAlert({
                hidden: false,
                severity: AlertType.warning,
                message: TEMPLATE_REGENERATION_TIMEOUT_MESSAGE,
            }));
        },
        onError: (templateId: number) => {
            removeGeneratingTemplateId(templateId);
            setGenerationResultMessage(templateId, AlertType.error, TEMPLATE_REGENERATION_STATUS_CHECK_FAILED_MESSAGE);
            dispatch(showAlert({
                hidden: false,
                severity: AlertType.error,
                message: TEMPLATE_REGENERATION_STATUS_CHECK_FAILED_MESSAGE,
            }));
        },
    });

    // Separate polling hook instance (keyed by caseId) for Associated templates' per-case
    // regeneration, so it can run independently/concurrently alongside the standalone-template poll.
    // No explicit checkCompletion override is passed - the hook's default (getCaseTemplateAsync +
    // truthy templateDocumentId check) already matches this scenario exactly.
    const { startPolling: startCaseTemplateGenerationPolling } = useTemplateGenerationPolling({
        onCompleted: (caseId: number) => {
            handleCaseGenerationSettled(caseId, true);
        },
        onTimedOut: (caseId: number) => {
            handleCaseGenerationSettled(caseId, false);
        },
        onError: (caseId: number) => {
            handleCaseGenerationSettled(caseId, false);
        },
    });

    const handleGenerateAssociatedTemplate = (templateId: number) => {
        // Associated templates pursue 2 separate paths in the generator service: the standalone
        // template-id-only regeneration below, plus the per-case (templateId + caseId) loop
        // further down. Both are required for Associated templates.
        const standaloneGeneratePromise = generateTemplateAsync(templateId);
        standaloneGeneratePromise.catch(error => console.error(error));

        getTemplateDetailAsync(templateId)
            .then(templateDetail => {
                const cases: ICase[] = templateDetail?.caseList ?? [];

                if (cases.length === 0) {
                    // No associated cases to fan out to - fall back to tracking the standalone
                    // regeneration request so the row's generating state/completion is still
                    // accurately reflected instead of being dropped/abandoned.
                    standaloneGeneratePromise
                        .then(response => {
                            if (response.result) {
                                startTemplateGenerationPolling(templateId);
                            } else {
                                removeGeneratingTemplateId(templateId);
                                const message = response.message ?? TEMPLATE_REGENERATION_FAILED_MESSAGE;
                                setGenerationResultMessage(templateId, AlertType.error, message);
                                dispatch(showAlert({
                                    hidden: false,
                                    severity: AlertType.error,
                                    message,
                                }));
                            }
                        })
                        .catch(error => {
                            console.error(error);
                            removeGeneratingTemplateId(templateId);
                            setGenerationResultMessage(templateId, AlertType.error, TEMPLATE_REGENERATION_FAILED_MESSAGE);
                            dispatch(showAlert({
                                hidden: false,
                                severity: AlertType.error,
                                message: TEMPLATE_REGENERATION_FAILED_MESSAGE,
                            }));
                        });
                    return;
                }

                templateCaseProgressRef.current.set(templateId, { pending: cases.length, failedCases: [] });

                cases.forEach(associatedCase => {
                    caseGenerationContextRef.current.set(associatedCase.id, {
                        templateId,
                        caseName: associatedCase.name || associatedCase.number || `Case ${associatedCase.id}`,
                    });

                    const payload: ICaseTemplate = { caseId: associatedCase.id, templateId };

                    saveCaseTemplate(payload)
                        .then(result => {
                            if (result) {
                                startCaseTemplateGenerationPolling(associatedCase.id);
                            } else {
                                handleCaseGenerationSettled(associatedCase.id, false);
                            }
                        })
                        .catch(error => {
                            console.error(error);
                            handleCaseGenerationSettled(associatedCase.id, false);
                        });
                });
            })
            .catch(error => {
                console.error(error);
                removeGeneratingTemplateId(templateId);
                setGenerationResultMessage(templateId, AlertType.error, TEMPLATE_REGENERATION_FAILED_MESSAGE);
                dispatch(showAlert({
                    hidden: false,
                    severity: AlertType.error,
                    message: TEMPLATE_REGENERATION_FAILED_MESSAGE,
                }));
            });
    };

    const handleGenerateTemplate = (templateId: number, templateStatusId?: TemplateStatus) => {
        if (generatingTemplateIds.has(templateId)) {
            return;
        }

        addGeneratingTemplateId(templateId);
        clearGenerationResultMessage(templateId);

        if (templateStatusId === TemplateStatus.Associated) {
            handleGenerateAssociatedTemplate(templateId);
            return;
        }

        generateTemplateAsync(templateId)
            .then(response => {
                if (response.result) {
                    startTemplateGenerationPolling(templateId);
                } else {
                    removeGeneratingTemplateId(templateId);
                    const message = response.message ?? TEMPLATE_REGENERATION_FAILED_MESSAGE;
                    setGenerationResultMessage(templateId, AlertType.error, message);
                    dispatch(showAlert({
                        hidden: false,
                        severity: AlertType.error,
                        message,
                    }));
                }
            })
            .catch(error => {
                console.error(error);
                removeGeneratingTemplateId(templateId);
                setGenerationResultMessage(templateId, AlertType.error, TEMPLATE_REGENERATION_FAILED_MESSAGE);
                dispatch(showAlert({
                    hidden: false,
                    severity: AlertType.error,
                    message: TEMPLATE_REGENERATION_FAILED_MESSAGE,
                }));
            });
    };

    const handleClick = (event: React.MouseEvent<HTMLElement>, index: number) => {
        setKabobsMenu(event.currentTarget);
        setSelectedRowIndex(index);
    };

    const [modalOpen, setModalOpen] = useState<boolean>(false);

    const handleClose = () => {
        setKabobsMenu(null);
        setSelectedRowIndex(-1);
    };

    if (isInitialized && isAuthenticated && user.isEpiqUser)
        if (
            isInRole([
                EpiqAdmin,
                EpiqProjectManager,
                EpiqTechSupport,
                EpiqTemplateAdmin,
            ])
        )
            showKebobs = true;

    useEffect(() => {
        if (templateState.needsRefresh) {
            templateState.needsRefresh = false;

            let newFilter = getFilterFromProps();
            addTemplateTypeFilter(newFilter);
            dispatch(getTemplatesAction({ filter: newFilter }));
        }
    });

    useEffect(() => {
        addTemplateTypeFilter(initialFilter);
        dispatch(getTemplatesAction({ filter: initialFilter }));
    }, [dispatch]);

    useEffect(() => {
        let newFilter = getFilterFromProps();
        addTemplateTypeFilter(newFilter);
        dispatch(getTemplatesAction({ filter: newFilter }));
    }, [searchName, searchStatus, dispatch]);

    const fetchFilteredTemplateList = (filters: IFilterDTO[]) => {
        addTemplateTypeFilter(filters);
        dispatch(getTemplatesAction({ filter: filters }));
    };

    const handleResetFilter = () => {
        sortInfo = { ...defaultSort };
    };

    const handleDelete = (id: number, name: string) => {
        setSelectedTemplateId(id);
        setSelectedTemplateName(name);
        setModalOpen(true);
    };

    const buildTemplateListRows = (): ISecuritiesTableFieldMap[] => {
        var templateListRows: ISecuritiesTableFieldMap[] = [];

        templateState.templates &&
            templateState.templates.forEach((item, index) => {
                const generationResultMessage = generationResultMessages[item.id];

                let newItem: ISecuritiesTableFieldMap = {
                    name: {
                        type: "link",
                        value: (
                            <Fragment>
                                <Link color="success" to={`/TemplateDetail/${item.id}`}>
                                    {item.name}
                                </Link>
                                {generationResultMessage && (
                                    <Alert
                                        severity={generationResultMessage.severity}
                                        onClose={() => clearGenerationResultMessage(item.id)}
                                        sx={{ mt: 1 }}
                                    >
                                        {generationResultMessage.message}
                                    </Alert>
                                )}
                            </Fragment>
                        ),
                        sortValue: item.name,
                    },
                    templateStatus: {
                        type: "string",
                        value: getTemplateStatusText(item.templateStatusId),
                    },
                    //templateStatus: { type: "string", value: TemplateStatus[item.templateStatusId] },
                    updatedOn: { type: "date", value: item.updatedOn },
                    updatedByUser: { type: "string", value: item.updatedByUser },
                    actions: {
                        type: "link",
                        value: (
                            <Fragment>
                                <div>
                                    <IconButton
                                        aria-label="more"
                                        //className={classes.kabobIconEnable}
                                        id={"long-button-" + item.id}
                                        aria-haspopup="true"
                                        sx={{ padding: "0" }}
                                        onClick={(event: React.MouseEvent<HTMLElement>) => {
                                            handleClick(event, index);
                                        }}
                                        disabled={!isInRole(TemplatePermissions.View)}
                                    >
                                        <MoreHorizIcon />
                                    </IconButton>
                                    {generatingTemplateIds.has(item.id) && (
                                        <Chip
                                            size="small"
                                            color="info"
                                            icon={<CircularProgress size={14} color="inherit" />}
                                            label="Generating..."
                                            sx={{ ml: 1 }}
                                        />
                                    )}

                                    <Menu
                                        anchorEl={kabobsMenu}
                                        open={selectedRowIndex === index}
                                        onClose={handleClose}
                                        onClick={handleClose}
                                        id={"long-menu-" + item.id}
                                        MenuListProps={{
                                            "aria-labelledby": "long-button-" + item.id,
                                        }}
                                        PaperProps={{
                                            style: {
                                                maxHeight: STANDARD_KABOB_HEIGHT * 4.5,
                                                width: "20ch",
                                            },
                                        }}
                                    >
                                        {(item.templateStatusId === TemplateStatus.Error ||
                                            item.templateStatusId === TemplateStatus.Development ||
                                            item.templateStatusId === TemplateStatus.Generating ||
                                            item.templateStatusId === TemplateStatus.Generated) && (
                                                <Box>
                                                    {isInRole(TemplatePermissions.View) && (
                                                        <MenuItem
                                                            onClick={() => {
                                                                history.push(`/TemplateDetail/${item.id}`);
                                                            }}
                                                        >
                                                            <Typography>View</Typography>
                                                        </MenuItem>
                                                    )}
                                                    {isInRole(TemplatePermissions.Edit) && (
                                                        <MenuItem
                                                            onClick={() => {
                                                                editTemplate(item.id);
                                                            }}
                                                        >
                                                            <Typography>Edit</Typography>
                                                        </MenuItem>
                                                    )}
                                                    {isInRole(TemplatePermissions.Regenerate) && (
                                                        <MenuItem
                                                            disabled={generatingTemplateIds.has(item.id)}
                                                            onClick={() => {
                                                                handleGenerateTemplate(item.id, item.templateStatusId);
                                                            }}
                                                        >
                                                            <Typography>{generatingTemplateIds.has(item.id) ? "Generating..." : "Generate"}</Typography>
                                                        </MenuItem>
                                                    )}
                                                    {isInRole(TemplatePermissions.Download) && (
                                                        <MenuItem
                                                            disabled={
                                                                item.templateStatusId !== TemplateStatus.Generated ||
                                                                !item.documentId
                                                            }
                                                            onClick={() => {
                                                                dispatch(downloadTemplateAction(item.id));
                                                            }}
                                                        >
                                                            <Typography>Download</Typography>
                                                        </MenuItem>
                                                    )}
                                                    {isInRole(TemplatePermissions.Duplicate) && (
                                                        <MenuItem
                                                            onClick={() => {
                                                                duplicateTemplate(item.id);
                                                            }}
                                                        >
                                                            <Typography>Duplicate</Typography>
                                                        </MenuItem>
                                                    )}
                                                    {isInRole(TemplatePermissions.Approve) && (
                                                        <MenuItem
                                                            disabled={
                                                                item.templateStatusId !== TemplateStatus.Generated
                                                            }
                                                            onClick={() => {
                                                                dispatch(
                                                                    approveTemplateAction({
                                                                        templateId: item.id,
                                                                        updatedBy: user.id,
                                                                    })
                                                                );
                                                            }}
                                                        >
                                                            <Typography>Approve</Typography>
                                                        </MenuItem>
                                                    )}
                                                    {isInRole(TemplatePermissions.Delete) && (
                                                        <MenuItem
                                                            onClick={() => {
                                                                handleDelete(item.id, item.name);
                                                            }}
                                                        >
                                                            <Typography>Delete</Typography>
                                                        </MenuItem>
                                                    )}
                                                </Box>
                                            )}
                                        {item.templateStatusId === TemplateStatus.Associated && (
                                            <Box>
                                                {isInRole(TemplatePermissions.View) && (
                                                    <MenuItem
                                                        onClick={() => {
                                                            history.push(`/TemplateDetail/${item.id}`);
                                                        }}
                                                    >
                                                        <Typography>View</Typography>
                                                    </MenuItem>
                                                )}
                                                {isInRole(TemplatePermissions.Regenerate) && (
                                                    <MenuItem
                                                        disabled={generatingTemplateIds.has(item.id)}
                                                        onClick={() => {
                                                            handleGenerateTemplate(item.id, item.templateStatusId);
                                                        }}
                                                    >
                                                        <Typography>{generatingTemplateIds.has(item.id) ? "Generating..." : "Generate"}</Typography>
                                                    </MenuItem>
                                                )}
                                                {isInRole(TemplatePermissions.Download) && (
                                                    <MenuItem
                                                        disabled={!item.documentId}
                                                        onClick={() => {
                                                            dispatch(downloadTemplateAction(item.id));
                                                        }}
                                                    >
                                                        <Typography>Download</Typography>
                                                    </MenuItem>
                                                )}
                                                {isInRole(TemplatePermissions.Duplicate) && (
                                                    <MenuItem
                                                        onClick={() => {
                                                            duplicateTemplate(item.id);
                                                        }}
                                                    >
                                                        <Typography>Duplicate</Typography>
                                                    </MenuItem>
                                                )}
                                            </Box>
                                        )}
                                        {item.templateStatusId === TemplateStatus.Approved && (
                                            <Box>
                                                {isInRole(TemplatePermissions.View) && (
                                                    <MenuItem
                                                        onClick={() => {
                                                            history.push(`/TemplateDetail/${item.id}`);
                                                        }}
                                                    >
                                                        <Typography>View</Typography>
                                                    </MenuItem>
                                                )}
                                                {isInRole(TemplatePermissions.Regenerate) && (
                                                    <MenuItem
                                                        disabled={generatingTemplateIds.has(item.id)}
                                                        onClick={() => {
                                                            handleGenerateTemplate(item.id, item.templateStatusId);
                                                        }}
                                                    >
                                                        <Typography>{generatingTemplateIds.has(item.id) ? "Generating..." : "Generate"}</Typography>
                                                    </MenuItem>
                                                )}
                                                {isInRole(TemplatePermissions.Download) && (
                                                    <MenuItem
                                                        disabled={!item.documentId}
                                                        onClick={() => {
                                                            dispatch(downloadTemplateAction(item.id));
                                                        }}
                                                    >
                                                        <Typography>Download</Typography>
                                                    </MenuItem>
                                                )}
                                                {isInRole(TemplatePermissions.Duplicate) && (
                                                    <MenuItem
                                                        onClick={() => {
                                                            duplicateTemplate(item.id);
                                                        }}
                                                    >
                                                        <Typography>Duplicate</Typography>
                                                    </MenuItem>
                                                )}
                                                {isInRole(TemplatePermissions.Approve) && (
                                                    <MenuItem
                                                        onClick={() => {
                                                            dispatch(
                                                                denyTemplateAction({
                                                                    templateId: item.id,
                                                                    updatedBy: user.id,
                                                                })
                                                            );
                                                        }}
                                                    >
                                                        <Typography>Unapprove</Typography>
                                                    </MenuItem>
                                                )}
                                            </Box>
                                        )}
                                    </Menu>
                                </div>
                            </Fragment>
                        ),
                    },
                };

                templateListRows.push(newItem);
            });

        return templateListRows;
    };

    let rows = null as ISecuritiesTableFieldMap[] | null;

    if (!templateState.error) rows = buildTemplateListRows();

    function duplicateTemplate(templateId: number) {
        handleDuplicateTemplate(templateId);
    }

    function editTemplate(templateId: number) {
        handleEditTemplate(templateId);
    }

    return (
        <>
            {templateState.isLoading === true ? (
                <Card>
                    <CardContent>
                        {[...Array(5)].map((elementInArray, index) => (
                            <Grid container spacing={2} key={index}>
                                <Grid item xs={2}>
                                    <Skeleton animation="wave" />
                                </Grid>

                                <Grid item xs={4}>
                                    <Skeleton animation="wave" />
                                </Grid>

                                <Grid item xs={2}>
                                    <Skeleton animation="wave" />
                                </Grid>

                                <Grid item xs={2}>
                                    <Skeleton animation="wave" />
                                </Grid>
                            </Grid>
                        ))}
                    </CardContent>
                </Card>
            ) : (
                <>
                    <SecuritiesTable
                        columns={templateListFilterColumns}
                        rows={rows}
                        sorter={sortInfo}
                        paging={"client"}
                        dataFetchFunc={fetchFilteredTemplateList}
                        resetFilterFunc={handleResetFilter}
                    />
                    <DeleteTemplateModal
                        modalOpen={modalOpen}
                        templateId={selectedTemplateId}
                        templateName={selectedTemplateName}
                        handleDelete={(id: number) => {
                            setModalOpen(false);
                            dispatch(deleteTemplateAction(id));
                        }}
                        handleCancelClose={() => {
                            setModalOpen(false);
                            setSelectedTemplateId(null);
                            setSelectedTemplateName("");
                        }}
                    />
                </>
            )}
        </>
    );
};

export default TemplateListGrid;

